/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Button, CardContent, CircularProgress, Typography,
  Chip, Tooltip, ToggleButtonGroup, ToggleButton,
  Select, MenuItem, FormControl, InputLabel, Alert, Skeleton,
} from "@mui/material";
import GlassCard from "../Cards/GlassCard";
import AccessTimeRoundedIcon    from "@mui/icons-material/AccessTimeRounded";
import BarChartRoundedIcon      from "@mui/icons-material/BarChartRounded";
import SyncRoundedIcon          from "@mui/icons-material/SyncRounded";
import TrendingUpRoundedIcon    from "@mui/icons-material/TrendingUpRounded";
import CommonDataGrid           from "./CommonDataGrid";
import axiosInstance            from "../../services/axiosInstance";
import {
  gradientTextSx, PRIMARY, SUCCESS, WARNING, DANGER,
} from "../../utils/colors";

// ── helpers ───────────────────────────────────────────────────────────────────
const fmtSyncedAt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return null; }
};

/** Returns { fromDate, toDate } as YYYY-MM-DD strings for "last N weeks" */
const getWeekPreset = (weeks) => {
  const to   = new Date();
  const from = new Date();
  from.setUTCDate(from.getUTCDate() - weeks * 7);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { fromDate: fmt(from), toDate: fmt(to) };
};

const WEEK_PRESETS = [
  { label: "Last 1 week",   value: 1  },
  { label: "Last 4 weeks",  value: 4  },
  { label: "Last 12 weeks", value: 12 },
];

// ── Column definitions ────────────────────────────────────────────────────────

const baseColumns = [
  {
    field: "rank", headerName: "Rank", width: 70,
    sortable: false, align: "center", headerAlign: "center",
    renderCell: ({ value }) => (
      <Typography variant="body2" fontWeight={700} color="text.secondary">#{value}</Typography>
    ),
  },
  {
    field: "name", headerName: "Student", width: 180,
    renderCell: ({ value }) => (
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    ),
  },
  {
    field: "rollNo", headerName: "Roll No.", width: 110,
    align: "center", headerAlign: "center",
    renderCell: ({ value }) => (
      <Typography variant="body2" color="text.secondary">{value ?? "—"}</Typography>
    ),
  },
];

const globalStatsColumns = [
  {
    field: "easySolved", headerName: "Easy ✅", width: 88,
    type: "number", align: "center", headerAlign: "center",
    renderCell: ({ value }) => (
      <Typography variant="body2" fontWeight={700} sx={{ color: SUCCESS }}>{value ?? 0}</Typography>
    ),
  },
  {
    field: "mediumSolved", headerName: "Medium 🟡", width: 104,
    type: "number", align: "center", headerAlign: "center",
    renderCell: ({ value }) => (
      <Typography variant="body2" fontWeight={700} sx={{ color: WARNING }}>{value ?? 0}</Typography>
    ),
  },
  {
    field: "hardSolved", headerName: "Hard 🔴", width: 88,
    type: "number", align: "center", headerAlign: "center",
    renderCell: ({ value }) => (
      <Typography variant="body2" fontWeight={700} sx={{ color: DANGER }}>{value ?? 0}</Typography>
    ),
  },
  {
    field: "leetcodeTotalSolved", headerName: "LeetCode Total Solved", width: 155,
    type: "number", align: "center", headerAlign: "center",
    renderCell: ({ value }) => (
      <Tooltip title="Total problems solved on LeetCode (official count)" arrow>
        <Chip label={value ?? 0} size="small"
          sx={{ bgcolor: `${PRIMARY}18`, color: PRIMARY, fontWeight: 800, fontSize: "0.8rem" }} />
      </Tooltip>
    ),
  },
  {
    field: "contestRating", headerName: "Contest Rating", width: 130,
    type: "number", align: "center", headerAlign: "center",
    renderCell: ({ value }) => value != null
      ? <Chip label={Math.round(value)} size="small"
          sx={{ bgcolor: "action.selected", fontWeight: 700, fontSize: "0.72rem" }} />
      : <Typography variant="body2" color="text.disabled">—</Typography>,
  },
  {
    field: "lastSyncedAt", headerName: "Last Synced", width: 170,
    sortable: true, align: "center", headerAlign: "center",
    renderCell: ({ value }) => {
      const label = fmtSyncedAt(value);
      return label
        ? (
          <Tooltip title={`Synced from LeetCode at ${label}`} arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeRoundedIcon sx={{ fontSize: 13, color: "text.disabled" }} />
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          </Tooltip>
        )
        : (
          <Tooltip title="Not synced yet" arrow>
            <Typography variant="caption" color="error.main" fontWeight={600}>Not synced</Typography>
          </Tooltip>
        );
    },
  },
];

/** Weekly-progress table columns. When API uses daily `progress_snapshots`, snapshotCount is days — not weeks. */
const getWeeklyProgressColumns = (serverUsesDailySnapshots) => {
  const snapshotHeader = serverUsesDailySnapshots ? "Days of data" : "Weeks of data";
  const fromHeader = serverUsesDailySnapshots ? "From date" : "From week";
  const toHeader = serverUsesDailySnapshots ? "To date" : "To week";
  const totalTooltip = serverUsesDailySnapshots
    ? "Total problems solved on LeetCode (official count, as of latest snapshot in range)"
    : "Total problems solved on LeetCode (official count, as of latest weekly snapshot)";

  return [
    {
      field: "progress", headerName: "Problems Gained 📈", width: 160,
      type: "number", align: "center", headerAlign: "center",
      renderCell: ({ value }) => (
        <Chip
          label={`+${value ?? 0}`}
          size="small"
          sx={{
            bgcolor: value > 0 ? `${SUCCESS}22` : "action.selected",
            color:   value > 0 ? SUCCESS        : "text.secondary",
            fontWeight: 800, fontSize: "0.82rem",
          }}
        />
      ),
    },
    {
      field: "leetcodeTotalSolved", headerName: "LeetCode Total Solved", width: 155,
      type: "number", align: "center", headerAlign: "center",
      renderCell: ({ value }) => (
        <Tooltip title={totalTooltip} arrow>
          <Chip label={value ?? 0} size="small"
            sx={{ bgcolor: `${PRIMARY}18`, color: PRIMARY, fontWeight: 800, fontSize: "0.8rem" }} />
        </Tooltip>
      ),
    },
    {
      field: "snapshotCount", headerName: snapshotHeader, width: 130,
      type: "number", align: "center", headerAlign: "center",
      renderCell: ({ value, row }) => {
        const n = value ?? 0;
        const isDaily = row?.progressSource === "daily";
        const label = isDaily
          ? `${n} day${n !== 1 ? "s" : ""}`
          : `${n} week${n !== 1 ? "s" : ""}`;
        return (
          <Typography variant="body2" color="text.secondary">{label}</Typography>
        );
      },
    },
    {
      field: "earliestWeek", headerName: fromHeader, width: 120,
      align: "center", headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography variant="caption" color="text.secondary">{value ?? "—"}</Typography>
      ),
    },
    {
      field: "latestWeek", headerName: toHeader, width: 120,
      align: "center", headerAlign: "center",
      renderCell: ({ value }) => (
        <Typography variant="caption" color="text.secondary">{value ?? "—"}</Typography>
      ),
    },
  ];
};

// ── Component ─────────────────────────────────────────────────────────────────
const LeetCodeAnalyticsTable = ({ rows = [], loading = false, onRowClick, onSync, batchId }) => {
  const [mode,         setMode]         = useState("globalStats");   // "globalStats" | "weeklyProgress"
  const [weekPreset,   setWeekPreset]   = useState(4);               // 1 | 4 | 12
  const [progressRows, setProgressRows] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError,   setProgressError]   = useState(null);

  // ── Fetch progress data when mode = weeklyProgress ────────────────────────
  useEffect(() => {
    if (mode !== "weeklyProgress" || !batchId) return;

    const { fromDate, toDate } = getWeekPreset(weekPreset);
    setProgressLoading(true);
    setProgressError(null);

    axiosInstance
      .get(`/batch/${batchId}/leetcode-progress`, { params: { fromDate, toDate } })
      .then(({ data }) => {
        const students = data?.students ?? [];
        // Re-rank by progress DESC (server already sorts but re-rank for DataGrid)
        const ranked = students.map((s, i) => ({
          ...s,
          id:   s.studentId,
          rank: i + 1,
        }));
        setProgressRows(ranked);
      })
      .catch((err) => {
        setProgressError(err?.response?.data?.message ?? "Failed to load weekly progress");
        setProgressRows([]);
      })
      .finally(() => setProgressLoading(false));
  }, [mode, weekPreset, batchId]);

  // ── Active data / columns based on mode ──────────────────────────────────
  const activeRows    = mode === "globalStats" ? rows          : progressRows;
  const activeLoading = mode === "globalStats" ? loading       : progressLoading;
  const activeSort    = mode === "globalStats"
    ? [{ field: "leetcodeTotalSolved", sort: "desc" }]
    : [{ field: "progress",    sort: "desc" }];

  const serverUsesDailySnapshots =
    mode === "weeklyProgress" && progressRows.length > 0 && progressRows[0]?.progressSource === "daily";

  const columns = useMemo(() =>
    [...baseColumns, ...(mode === "globalStats"
      ? globalStatsColumns
      : getWeeklyProgressColumns(serverUsesDailySnapshots))],
    [mode, serverUsesDailySnapshots]
  );

  return (
    <GlassCard
      hoverable={false}
      sx={{ mt: 3, borderRadius: 4, overflow: "hidden" }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2.5 } }}>

        {/* ── Header row ── */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between"
          flexWrap="wrap" gap={2} mb={1.5}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ ...gradientTextSx }}>
              {mode === "globalStats"
                ? "LeetCode Performance — Global Stats"
                : "LeetCode Performance — Weekly Progress"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {mode === "globalStats"
                ? "Sourced from LeetCode profile sync · Not related to assignment completion"
                : "Progress in the selected date range · from daily or weekly snapshots (see table headers)"}
            </Typography>
          </Box>
        </Box>

        {/* ── Mode toggle + week preset ── */}
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, v) => { if (v) setMode(v); }}
            size="small"
            sx={{ "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 2 } }}
          >
            <ToggleButton value="globalStats">
              <BarChartRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Global Stats
            </ToggleButton>
            <ToggleButton value="weeklyProgress">
              <TrendingUpRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
              Weekly Progress
            </ToggleButton>
          </ToggleButtonGroup>

          {mode === "weeklyProgress" && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Date range</InputLabel>
              <Select
                value={weekPreset}
                label="Date range"
                onChange={(e) => setWeekPreset(Number(e.target.value))}
                sx={{ borderRadius: 2 }}
              >
                {WEEK_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* ── Weekly mode banner ── */}
        {mode === "weeklyProgress" && (
          <Alert severity="info" variant="outlined" icon={<TrendingUpRoundedIcon />}
            sx={{ mb: 1.5, borderRadius: 2, py: 0.5 }}
          >
            <Typography variant="caption" fontWeight={600}>
              {serverUsesDailySnapshots ? (
                <>
                  Showing <strong>daily</strong> snapshots from{" "}
                  <strong>{getWeekPreset(weekPreset).fromDate}</strong> to{" "}
                  <strong>{getWeekPreset(weekPreset).toDate}</strong>.
                  {" "}Progress = latest total solved − earliest total solved in that range
                  (one row per UTC day when synced).
                </>
              ) : (
                <>
                  Showing <strong>weekly</strong> aggregated snapshots from{" "}
                  <strong>{getWeekPreset(weekPreset).fromDate}</strong> to{" "}
                  <strong>{getWeekPreset(weekPreset).toDate}</strong>.
                  {" "}Progress = latest week&apos;s total − earliest week&apos;s total (or prior baseline).
                </>
              )}
            </Typography>
          </Alert>
        )}

        {/* ── Error state ── */}
        {progressError && mode === "weeklyProgress" && (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>{progressError}</Alert>
        )}

        {/* ── DataGrid / Loading / Empty ── */}
        {activeLoading ? (
          /* Centered spinner + skeleton rows — table is hidden while loading */
          <Box sx={{ height: 600, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
              <CircularProgress size={36} thickness={4} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Loading analytics data…
              </Typography>
            </Box>
            {/* Skeleton rows to prevent layout shift */}
            <Box sx={{ px: 0.5, pb: 1 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 0.75, borderRadius: 1.5, opacity: 1 - i * 0.12 }} />
              ))}
            </Box>
          </Box>
        ) : activeRows.length === 0 ? (
          /* Empty state — no data after a successful load */
          <Box sx={{ height: 600, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1.5 }}>
            <BarChartRoundedIcon sx={{ fontSize: 56, color: "action.disabled" }} />
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary">
              No LeetCode data available
            </Typography>
            <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={320}>
              {mode === "weeklyProgress"
                ? "No weekly snapshots found for the selected date range. Try a different range or sync first."
                : "No LeetCode analytics data found for this batch."}
            </Typography>
            {onSync && (
              <Button
                variant="contained"
                startIcon={<SyncRoundedIcon />}
                onClick={onSync}
                sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
              >
                Sync LeetCode Data
              </Button>
            )}
          </Box>
        ) : (
          /* Data — render the full grid */
          <Box sx={{ height: 600, width: "100%" }}>
            <CommonDataGrid
              firstColumnField="rank"
              rows={activeRows}
              columns={columns}
              loading={false}
              pageSizeOptions={[10, 25, 50]}
              defaultPageSize={10}
              initialState={{ sorting: { sortModel: activeSort } }}
              enableSearch
              disableRowSelectionOnClick
              onRowClick={onRowClick ? ({ row }) => onRowClick(row) : undefined}
              sx={{ ...(onRowClick && { "& .MuiDataGrid-row": { cursor: "pointer" } }) }}
            />
          </Box>
        )}

      </CardContent>
    </GlassCard>
  );
};

export default LeetCodeAnalyticsTable;
